// SurveyJS schema for the EFS "Short Form Financial Fact Finder" notify-only intake.
// Embedded in the bundle. /api/public/efs-fact-finder-response/:id stores the server's
// copy of this file (helper/firmFormRoutes.js), not the posted `form_data`, as for
// views/website/mckinley-form.js. The TFN cells in `tfn_details` are redacted
// server-side and delivered via OneTimeSecret — see helper/redactTfn.js.
const efsFactFinderForm = {
  title: "Short Form Financial Fact Finder",
  description: "All About You Questionnaire — Financial Fact Finder",
  logoPosition: "right",
  completedHtml:
    "<div style='text-align:center;padding:40px 24px;'><h2>Thank you</h2><p>Your financial fact finder has been submitted.</p></div>",
  pages: [
    {
      name: "introduction",
      title: "Introduction",
      elements: [
        {
          type: "html",
          name: "intro_text",
          html: "<p>To enable us to provide you with financial advice, it is important that we understand your goals, objectives and financial situation. This form is designed to record this information.</p>",
        },
        {
          type: "text",
          name: "client_name",
          title: "Client name",
          isRequired: true,
        },
        {
          type: "text",
          name: "financial_adviser",
          title: "Financial adviser",
        },
        {
          type: "text",
          name: "form_date",
          title: "Date",
          defaultValueExpression: "today()",
          inputType: "date",
        },
      ],
    },
    {
      name: "your_profile",
      title: "Your Profile",
      elements: [
        {
          type: "comment",
          name: "reason_for_advice",
          title: "Why are you seeking advice?",
          description:
            "What are your reasons for seeking financial advice? What are you looking to achieve?",
        },
        {
          type: "comment",
          name: "cashflow_changes",
          title: "Cashflow - Changes",
          description:
            "Please provide any additional information that you think may be relevant for our meeting.",
        },
        {
          type: "paneldynamic",
          name: "goals",
          title: "Goals",
          templateElements: [
            { type: "text", name: "goal", title: "Goal" },
            { type: "text", name: "timeframe", title: "Timeframe" },
          ],
          templateTitle: "Goal {panelIndex}",
          panelCount: 1,
          minPanelCount: 1,
          maxPanelCount: 4,
        },
        {
          type: "comment",
          name: "money_growing_up",
          title: "What was money like growing up for you?",
        },
        {
          type: "comment",
          name: "best_worst_financial_experience",
          title: "What has been your best and worst financial experience?",
        },
        {
          type: "comment",
          name: "financial_professional_experience",
          title:
            "Have you had any good or bad experiences with financial professionals?",
        },
        {
          type: "comment",
          name: "expenditure_changes",
          title: "Expenditure - Changes",
          description:
            "Please provide any additional information that you think may be relevant for our meeting.",
        },
      ],
    },
    {
      name: "personal_details",
      title: "Personal Details",
      elements: [
        {
          type: "panel",
          name: "client_personal_details",
          title: "Client",
          elements: [
            {
              type: "dropdown",
              name: "client_title",
              title: "Title",
              choices: ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"],
            },
            { type: "text", name: "client_first_names", title: "First name(s)" },
            { type: "text", name: "client_surname", title: "Surname" },
            {
              type: "text",
              name: "client_date_of_birth",
              title: "Date of birth",
              inputType: "date",
            },
            {
              type: "dropdown",
              name: "client_gender",
              title: "Gender",
              choices: [
                "Male",
                "Female",
                "Non-binary",
                "Prefer not to say",
                "Other",
              ],
            },
            {
              type: "dropdown",
              name: "client_relationship_status",
              title: "Relationship status",
              choices: [
                "Single",
                "Married",
                "De facto",
                "Separated",
                "Divorced",
                "Widowed",
                "Other",
              ],
            },
          ],
        },
        {
          type: "boolean",
          name: "has_partner",
          title: "Do you have a partner whose details should be included?",
          defaultValue: "false",
        },
        {
          type: "panel",
          name: "partner_personal_details",
          visibleIf: "{has_partner} = true",
          title: "Partner",
          elements: [
            {
              type: "dropdown",
              name: "partner_title",
              title: "Title",
              choices: ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"],
            },
            { type: "text", name: "partner_first_names", title: "First name(s)" },
            { type: "text", name: "partner_surname", title: "Surname" },
            {
              type: "text",
              name: "partner_date_of_birth",
              title: "Date of birth",
              inputType: "date",
            },
            {
              type: "dropdown",
              name: "partner_gender",
              title: "Gender",
              choices: [
                "Male",
                "Female",
                "Non-binary",
                "Prefer not to say",
                "Other",
              ],
            },
            {
              type: "dropdown",
              name: "partner_relationship_status",
              title: "Relationship status",
              choices: [
                "Single",
                "Married",
                "De facto",
                "Separated",
                "Divorced",
                "Widowed",
                "Other",
              ],
            },
          ],
        },
      ],
    },
    {
      name: "health_and_dependants",
      title: "Health and Dependants",
      elements: [
        {
          type: "radiogroup",
          name: "client_health",
          title: "How would you describe your health?",
          choices: ["Excellent", "Good", "Average", "Poor"],
        },
        {
          type: "comment",
          name: "client_health_issues",
          title:
            "Please detail any health issues that could affect your goals and objectives",
        },
        {
          type: "radiogroup",
          name: "partner_health",
          visibleIf: "{has_partner} = true",
          title: "How would you describe your partner's health?",
          choices: ["Excellent", "Good", "Average", "Poor"],
        },
        {
          type: "comment",
          name: "partner_health_issues",
          visibleIf: "{has_partner} = true",
          title:
            "Please detail any partner health issues that could affect your goals and objectives",
        },
        {
          type: "boolean",
          name: "has_dependants",
          title: "Do you have any dependants or children?",
          defaultValue: "false",
        },
        {
          type: "paneldynamic",
          name: "dependants",
          visibleIf: "{has_dependants} = true",
          title: "Dependants",
          templateElements: [
            { type: "text", name: "name", title: "Name" },
            {
              type: "dropdown",
              name: "gender",
              title: "Gender",
              choices: [
                "Male",
                "Female",
                "Non-binary",
                "Prefer not to say",
                "Other",
              ],
            },
            {
              type: "text",
              name: "date_of_birth",
              title: "Date of birth",
              inputType: "date",
            },
            { type: "boolean", name: "living_at_home", title: "Living at home?" },
            { type: "boolean", name: "at_school", title: "At school?" },
          ],
          templateTitle: "Dependant {panelIndex}",
          panelCount: 1,
          minPanelCount: 1,
        },
      ],
    },
    {
      name: "contact_information",
      title: "Contact Information",
      elements: [
        {
          type: "panel",
          name: "residential_address",
          title: "Residential address",
          elements: [
            { type: "text", name: "residential_street", title: "Street no/name" },
            { type: "text", name: "residential_suburb", title: "Suburb" },
            { type: "text", name: "residential_state", title: "State" },
            { type: "text", name: "residential_postcode", title: "Postcode" },
          ],
        },
        {
          type: "panel",
          name: "postal_address",
          title: "Postal address",
          elements: [
            {
              type: "boolean",
              name: "postal_same_as_residential",
              title: "Postal address is the same as residential address",
              defaultValue: "true",
            },
            {
              type: "text",
              name: "postal_street",
              visibleIf: "{postal_same_as_residential} = false",
              title: "Street no/name",
            },
            {
              type: "text",
              name: "postal_suburb",
              visibleIf: "{postal_same_as_residential} = false",
              title: "Suburb",
            },
            {
              type: "text",
              name: "postal_state",
              visibleIf: "{postal_same_as_residential} = false",
              title: "State",
            },
            {
              type: "text",
              name: "postal_postcode",
              visibleIf: "{postal_same_as_residential} = false",
              title: "Postcode",
            },
          ],
        },
        {
          type: "matrixdropdown",
          name: "contact_details",
          title: "Contact details",
          columns: [
            { name: "client", title: "Client", cellType: "text" },
            {
              name: "client_preferred",
              title: "Client preferred",
              cellType: "boolean",
            },
            {
              name: "partner",
              title: "Partner",
              cellType: "text",
              visibleIf: "{has_partner} = true",
            },
            {
              name: "partner_preferred",
              title: "Partner preferred",
              cellType: "boolean",
              visibleIf: "{has_partner} = true",
            },
          ],
          rows: ["Home phone", "Mobile phone", "Work phone", "Email"],
        },
      ],
    },
    {
      name: "employment",
      title: "Employment",
      elements: [
        {
          type: "matrixdropdown",
          name: "employment_details",
          title: "Employment details",
          columns: [
            {
              name: "client",
              title: "Client",
              cellType: "dropdown",
              choices: ["Full Time", "Part time", "Casual", "Not working/retired"],
            },
            {
              name: "partner",
              title: "Partner",
              cellType: "dropdown",
              visibleIf: "{has_partner} = true",
              choices: ["Full Time", "Part time", "Casual", "Not working/retired"],
            },
          ],
          rows: ["Employment status"],
        },
        {
          type: "matrixdropdown",
          name: "employment_type",
          title: "Employment type",
          columns: [
            {
              name: "client",
              title: "Client",
              cellType: "dropdown",
              choices: ["Employee", "Self-employed", "Contractor", "Other"],
            },
            {
              name: "partner",
              title: "Partner",
              cellType: "dropdown",
              visibleIf: "{has_partner} = true",
              choices: ["Employee", "Self-employed", "Contractor", "Other"],
            },
          ],
          rows: ["Employment type"],
        },
        { type: "text", name: "client_occupation", title: "Client occupation" },
        {
          type: "text",
          name: "partner_occupation",
          visibleIf: "{has_partner} = true",
          title: "Partner occupation",
        },
      ],
    },
    {
      name: "income_and_savings",
      title: "Income and Savings",
      elements: [
        {
          type: "matrixdropdown",
          name: "income_details",
          title: "Income details",
          columns: [
            { name: "client", title: "Client", cellType: "text", inputType: "number" },
            { name: "partner", title: "Partner", cellType: "text", inputType: "number" },
            { name: "joint", title: "Joint", cellType: "text", inputType: "number" },
          ],
          rows: [
            "Gross salary",
            "Rental",
            "Investment: interest",
            "Investment: dividends",
            "Centrelink/DVA",
            "Super / Pension",
          ],
        },
        {
          type: "matrixdropdown",
          name: "savings_capacity",
          title: "Savings capacity",
          columns: [
            { name: "client", title: "Client", cellType: "text", inputType: "number" },
            { name: "partner", title: "Partner", cellType: "text", inputType: "number" },
            { name: "joint", title: "Joint", cellType: "text", inputType: "number" },
          ],
          rows: ["Gross salary"],
        },
      ],
    },
    {
      name: "assets",
      title: "Assets",
      elements: [
        {
          type: "matrixdropdown",
          name: "asset_details",
          title: "Asset details",
          columns: [
            { name: "client", title: "Client", cellType: "text", inputType: "number" },
            { name: "partner", title: "Partner", cellType: "text", inputType: "number" },
            { name: "joint", title: "Joint", cellType: "text", inputType: "number" },
          ],
          rows: ["Family home", "Contents", "Motor vehicles"],
        },
        {
          type: "checkbox",
          name: "lifestyle_asset_document_status",
          title: "Lifestyle assets statement status",
          choices: [
            "I/We have attached the relevant statement(s)",
            "I/We choose not to provide these details at this time",
            "I/We do not have any lifestyle assets at this time",
          ],
        },
        {
          type: "file",
          name: "lifestyle_asset_statements",
          title: "Upload lifestyle asset statement(s)",
          allowMultiple: true,
          maxSize: 10485760,
        },
        {
          type: "paneldynamic",
          name: "lifestyle_assets",
          title: "Lifestyle assets",
          templateElements: [
            {
              type: "dropdown",
              name: "owner",
              title: "Owner",
              choices: ["Client", "Partner", "Joint", "Other"],
            },
            { type: "text", name: "description", title: "Description" },
            {
              type: "text",
              name: "current_value",
              title: "Current value",
              inputType: "number",
            },
            {
              type: "text",
              name: "purchase_amount",
              title: "Purchase amount",
              inputType: "number",
            },
            {
              type: "text",
              name: "purchase_date",
              title: "Purchase date",
              inputType: "date",
            },
            {
              type: "boolean",
              name: "centrelink_asset_tested",
              title: "Centrelink asset tested?",
            },
            {
              type: "text",
              name: "centrelink_value",
              title: "Centrelink value",
              inputType: "number",
            },
            {
              type: "boolean",
              name: "security_for_loan",
              title: "Security for loan?",
            },
          ],
          templateTitle: "Lifestyle asset {panelIndex}",
        },
        {
          type: "matrixdropdown",
          name: "investment_assets",
          title: "Investment assets",
          columns: [
            { name: "client", title: "Client", cellType: "text", inputType: "number" },
            { name: "partner", title: "Partner", cellType: "text", inputType: "number" },
            { name: "joint", title: "Joint", cellType: "text", inputType: "number" },
          ],
          rows: [
            "Cash",
            "Term deposits",
            "Managed funds",
            "Shares",
            "Investment properties",
          ],
        },
        {
          type: "comment",
          name: "other_asset_notes",
          title: "Other asset notes",
          description:
            "If you have any other significant lifestyle assets, please include the details here.",
        },
      ],
    },
    {
      name: "liabilities",
      title: "Liabilities",
      elements: [
        {
          type: "checkbox",
          name: "liability_document_status",
          title: "Liability statement status",
          choices: [
            "I/We have attached the relevant statement(s)",
            "I/We choose not to provide these details at this time",
            "I/We do not have any liabilities at this time",
          ],
        },
        {
          type: "file",
          name: "liability_statements",
          title: "Upload liability statement(s)",
          allowMultiple: true,
          maxSize: 10485760,
        },
        {
          type: "paneldynamic",
          name: "question1",
          title: "Liabilities",
          templateElements: [
            {
              type: "dropdown",
              name: "question2",
              title: "Owner",
              choices: ["Client", "Partner", "Joint", "Other"],
            },
            { type: "text", name: "question3", title: "Description" },
            { type: "text", name: "loan_provider", title: "Loan provider" },
            {
              type: "text",
              name: "outstanding_balance",
              title: "Outstanding balance",
              inputType: "number",
            },
            {
              type: "text",
              name: "interest_rate",
              title: "Interest rate",
              inputType: "number",
            },
            { type: "text", name: "loan_term", title: "Loan term" },
            {
              type: "radiogroup",
              name: "loan_type",
              title: "Type",
              choices: ["Principal & Interest", "Interest Only"],
            },
            {
              type: "text",
              name: "repayment",
              title: "Repayment",
              inputType: "number",
            },
            {
              type: "dropdown",
              name: "frequency",
              title: "Frequency",
              choices: [
                "Weekly",
                "Fortnightly",
                "Monthly",
                "Quarterly",
                "Annually",
                "Other",
              ],
            },
            {
              type: "text",
              name: "interest_tax_deductible",
              title: "Interest tax deductible",
              inputType: "number",
            },
          ],
          templateTitle: "Liability {panelIndex}",
        },
      ],
    },
    {
      name: "superannuation",
      title: "Superannuation or Pension",
      elements: [
        {
          type: "paneldynamic",
          name: "superannuation_funds",
          title: "Superannuation or pension funds",
          templateElements: [
            {
              type: "dropdown",
              name: "question4",
              title: "Owner",
              choices: ["Client", "Partner", "Joint", "Other"],
            },
            { type: "text", name: "super_plan", title: "Super plan" },
            { type: "text", name: "reference_number", title: "Reference number" },
            {
              type: "text",
              name: "date_of_balance",
              title: "Date of balance",
              inputType: "date",
            },
            {
              type: "text",
              name: "date_commenced",
              title: "Date commenced",
              inputType: "date",
            },
            { type: "boolean", name: "death_nomination", title: "Death nomination?" },
            {
              type: "dropdown",
              name: "nomination_type",
              title: "Nomination type",
              choices: ["Discretionary", "Binding Lapsing", "Binding Nonlapsing"],
            },
            {
              type: "text",
              name: "binding_death_expiry_date",
              title: "Binding death expiry date",
              inputType: "date",
            },
            {
              type: "text",
              name: "beneficiary_names",
              title: "Beneficiary name(s)",
            },
            {
              type: "text",
              name: "relationship_of_beneficiary",
              title: "Relationship of beneficiary",
            },
            {
              type: "text",
              name: "beneficiary_percentage",
              title: "Percentage",
              inputType: "number",
            },
            {
              type: "text",
              name: "taxable_taxed",
              title: "Taxable - taxed",
              inputType: "number",
            },
            {
              type: "text",
              name: "taxable_untaxed",
              title: "Taxable - untaxed",
              inputType: "number",
            },
            { type: "text", name: "tax_free", title: "Tax free", inputType: "number" },
            { type: "text", name: "preserved", title: "Preserved", inputType: "number" },
            {
              type: "text",
              name: "restricted_non_preserved",
              title: "Restricted non preserved",
              inputType: "number",
            },
            {
              type: "text",
              name: "unrestricted_non_preserved",
              title: "Unrestricted non preserved",
              inputType: "number",
            },
            {
              type: "text",
              name: "total_balance",
              title: "Total balance",
              inputType: "number",
            },
          ],
          templateTitle: "Fund {panelIndex}",
        },
        {
          type: "comment",
          name: "superannuation_contributions_notes",
          title: "Superannuation contributions (p.a.)",
          description:
            "Please provide any additional information that you think may be relevant for our meeting.",
        },
      ],
    },
    {
      name: "risk_profile_1",
      title: "Risk & Return Profile Questionnaire",
      description:
        "The following questions will help determine your risk profile. Please answer for the relevant person/entity.",
      elements: [
        {
          type: "matrixdropdown",
          name: "risk_profile_responses_part_1",
          title: "Willingness to take on risk",
          columns: [
            { name: "client", title: "Client", cellType: "radiogroup", choices: ["Selected"] },
            { name: "partner", title: "Partner", cellType: "radiogroup", choices: ["Selected"] },
            { name: "joint", title: "Joint", cellType: "radiogroup", choices: ["Selected"] },
            { name: "entity", title: "Entity", cellType: "radiogroup", choices: ["Selected"] },
          ],
          rows: [
            {
              value: "q1_experienced",
              text: "Q1: I am an experienced investor. For example, I am familiar with investing and take an active interest in the share market.",
            },
            {
              value: "q1_good_understanding",
              text: "Q1: I have a good understanding. I know how my super or managed funds work and I know how my money is invested.",
            },
            {
              value: "q1_little_experience",
              text: "Q1: I have very little experience. I might know the balance of my super fund.",
            },
            { value: "q1_no_experience", text: "Q1: I have no experience." },
            {
              value: "q2_stable_low_fluctuation",
              text: "Q2: I prefer investments that are stable with low levels of fluctuation.",
            },
            {
              value: "q2_small_volatility",
              text: "Q2: I prefer stable investments with low fluctuation, but I am comfortable if a small portion experiences more volatility.",
            },
            {
              value: "q2_accept_fluctuation_some_stable",
              text: "Q2: I accept investment values going up and down, but would like some stable investments to smooth returns.",
            },
            {
              value: "q2_high_fluctuation",
              text: "Q2: I expect portfolio values to go up and down and am comfortable investing most of my portfolio in higher fluctuation assets.",
            },
            {
              value: "q3_any_fall",
              text: "Q3: Any fall would make me feel uncomfortable or question my decision to invest.",
            },
            {
              value: "q3_10000_fall",
              text: "Q3: A $10,000 fall to $90,000 would make me feel uncomfortable.",
            },
            {
              value: "q3_20000_fall",
              text: "Q3: A $20,000 fall to $80,000 would make me feel uncomfortable.",
            },
            {
              value: "q3_30000_fall",
              text: "Q3: A $30,000 fall to $70,000 would make me feel uncomfortable.",
            },
            {
              value: "q4_take_more_risk",
              text: "Q4: Take more risk within my portfolio, with the hope of achieving more of my goals.",
            },
            {
              value: "q4_maintain_strategy",
              text: "Q4: Continue to maintain my investment strategy.",
            },
            {
              value: "q4_reduce_risk",
              text: "Q4: Sell down part of my investments to reduce the chance of jeopardising my goals, even if this means accepting a lower return.",
            },
          ],
        },
      ],
    },
    {
      name: "risk_profile_2",
      title: "Risk & Return Profile Questionnaire Continued",
      elements: [
        {
          type: "matrixdropdown",
          name: "risk_profile_responses_part_2",
          title: "Risk and return preferences",
          columns: [
            { name: "client", title: "Client", cellType: "radiogroup", choices: ["Selected"] },
            { name: "partner", title: "Partner", cellType: "radiogroup", choices: ["Selected"] },
            { name: "joint", title: "Joint", cellType: "radiogroup", choices: ["Selected"] },
            { name: "entity", title: "Entity", cellType: "radiogroup", choices: ["Selected"] },
          ],
          rows: [
            { value: "q5_increase_risk", text: "Q5: Increase the risk to pursue my goals." },
            {
              value: "q5_maintain_strategy",
              text: "Q5: Continue to maintain my investment strategy to ride out the negative returns.",
            },
            {
              value: "q5_reduce_risk",
              text: "Q5: Reduce the risk within my portfolio to avoid further jeopardising my goals.",
            },
            {
              value: "q5_sell_investments",
              text: "Q5: Sell my investments to avoid any further losses, even if it means my goals will not be met.",
            },
            {
              value: "q6_immediately",
              text: "Q6: Move to a more conservative investment immediately.",
            },
            { value: "q6_12_months", text: "Q6: 12 months." },
            { value: "q6_2_years", text: "Q6: 2 years." },
            { value: "q6_5_years", text: "Q6: 5 years." },
            { value: "q6_7_years_plus", text: "Q6: 7 years +." },
            {
              value: "q7_1_in_5",
              text: "Q7: Comfortable with a 1 in 5 chance, or 20%, of a negative return in any one year.",
            },
            {
              value: "q7_1_in_12",
              text: "Q7: Comfortable with a 1 in 12 chance, or 8%, of a negative return in any one year.",
            },
            {
              value: "q7_1_in_33",
              text: "Q7: Comfortable with a 1 in 33 chance, or 3%, of a negative return in any one year.",
            },
            { value: "q7_zero", text: "Q7: Comfortable with 0% chance of a negative return." },
            {
              value: "q8_accumulate_wealth",
              text: "Q8: Investor with few financial commitments. I am looking to accumulate wealth for the future.",
            },
            {
              value: "q8_young_family",
              text: "Q8: Investor with financial commitments. I do not have a lot of money at the moment, but want to save more.",
            },
            {
              value: "q8_well_established",
              text: "Q8: Well established. My finances are under control. I want to save more and am considering retirement funding.",
            },
            {
              value: "q8_preparing_retirement",
              text: "Q8: Preparing for retirement. I am thinking of downsizing and releasing retirement funds.",
            },
            {
              value: "q8_retired",
              text: "Q8: Retired. I depend on my investments and am keen to maintain lifestyle.",
            },
          ],
        },
      ],
    },
    {
      name: "client_acknowledgement",
      title: "Client Acknowledgement and Declaration",
      elements: [
        {
          type: "html",
          name: "acknowledgement_text",
          html: "<p>I/We acknowledge that the information in this questionnaire is, to the best of my/our knowledge, an accurate and complete record of the information sought.</p><p>I/We acknowledge that if incomplete or limited financial information has been provided, there is a possibility that any recommendation may not be fully appropriate to my/our individual goals and needs.</p><p>I/We understand that any tools, calculators or worksheets used are illustrative tools and that personal advice will be provided in a written Statement of Advice.</p>",
        },
        {
          type: "checkbox",
          name: "fsg_delivery_preference",
          title:
            "My/Our preference for being provided with a copy of FSG Part 1 and Part 2 is by:",
          choices: ["Hand", "Mail", "Fax", "Email", "Website"],
        },
        {
          type: "checkbox",
          name: "privacy_policy_delivery_preference",
          title: "My/Our preference for receiving a copy of the Privacy Policy is by:",
          choices: ["Hand", "Mail", "Fax", "Email", "Website"],
        },
        {
          type: "boolean",
          name: "acknowledge_declaration",
          title: "I/We acknowledge and agree to the above declaration",
          isRequired: true,
        },
      ],
    },
    {
      name: "tfn_storage_consent",
      title: "Tax File Number Storage Consent",
      elements: [
        {
          type: "html",
          name: "tfn_consent_text",
          html: "<p>I/We permit EFS to retain my/our TFN on file, subject to the conditions outlined in the questionnaire, including use only for purposes approved by law, disclosure only to permitted parties, secure storage, and removal/destruction if instructed.</p>",
        },
        {
          type: "boolean",
          name: "provide_tfn_consent",
          title: "Do you consent to EFS retaining TFN details on file?",
          defaultValue: "false",
        },
        {
          type: "matrixdropdown",
          name: "tfn_details",
          visibleIf: "{provide_tfn_consent} = true",
          title: "TFN details",
          columns: [{ name: "tfn", title: "TFN", cellType: "text" }],
          rows: ["Client", "Partner", "SMSF", "Company", "Trust"],
        },
      ],
    },
    {
      name: "final_acknowledgement",
      title: "Final Acknowledgement",
      elements: [
        {
          type: "checkbox",
          name: "client_capacity",
          title: "Client capacity",
          choices: [
            "Individual",
            "Member/trustee of SMSF",
            "Director",
            "Authorised signatory",
            "Trustee",
            "Beneficiary of the Trust",
          ],
        },
        { type: "text", name: "client_acknowledgement_name", title: "Client name" },
        { type: "signaturepad", name: "client_signature", title: "Client signature" },
        {
          type: "text",
          name: "client_date_acknowledged",
          title: "Date acknowledged",
          inputType: "date",
        },
        {
          type: "text",
          name: "partner_acknowledgement_name",
          visibleIf: "{has_partner} = true",
          title: "Partner name",
        },
        {
          type: "signaturepad",
          name: "partner_signature",
          visibleIf: "{has_partner} = true",
          title: "Partner signature",
        },
        {
          type: "text",
          name: "partner_date_acknowledged",
          visibleIf: "{has_partner} = true",
          title: "Partner date acknowledged",
          inputType: "date",
        },
        { type: "text", name: "adviser_name", title: "Adviser name" },
        { type: "signaturepad", name: "adviser_signature", title: "Adviser signature" },
      ],
    },
  ],
  // survey-core expects a string enum here, not a boolean; "true" crashes Model
  // construction with `newValue.toLowerCase is not a function`.
  showProgressBar: "top",
};

// CommonJS, not "export": helper/firmFormRoutes.js requires this file as the
// server schema copy for the firm form route. The view keeps its named import.
module.exports = { efsFactFinderForm };
